/**
 * MXFCheck — controleert of een MXF-bestand voldoet aan de Screenforce/SpotDeli
 * technische aanleverspecificaties voor TV-commercials en billboards.
 *
 * Upload een .mxf, kies het contenttype (Commercial / Billboard / Promo), geef aan
 * wat je in het SpotDeli-metadataformulier zet (LOW_LOUDNESS_LEVEL /
 * MULTI_CHANNEL_AUDIO) en krijg per eis pass/fail te zien met hoe je het oplost.
 *
 * De analyse draait in de engine als achtergrondjob (/Api/mxf-check/analyses):
 * de upload geeft een job-id terug en deze pagina polt de status. Tijdens een
 * analyse ligt de invoer vast; elke invoerwijziging wist het vorige resultaat, en
 * een antwoord van een oudere run wordt genegeerd (runRef).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Collapse, Chip, Checkbox, FormControlLabel } from '@mui/material';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071',
  outline: '#767c7e', outlineVariant: '#c9cfd1',
  pass: '#2e7d51', passBg: '#e3f4ea',
  fail: '#c0392b', failBg: '#fbe6e3',
  warn: '#9a6a00', warnBg: '#fbf1dc',
  neutral: '#5a6570', neutralBg: '#eceff1',
  shadow: '0 10px 30px rgba(45,51,53,0.06)',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", "Menlo", "Consolas", monospace',
};

export const MAX_MB = 600;
const POLL_MS = 2000;
// Ruim boven wachtrij + analyse aan de serverkant (hooguit 6 jobs, 2 tegelijk, 10 min elk ≈ 30 min).
const MAX_POLL_MS = 45 * 60 * 1000;

const Icon = ({ name, fill, size = 22, sx = {} }) => (
  <span className="material-symbols-outlined"
    style={{ fontSize: size, fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 500`, lineHeight: 1, ...sx }}>
    {name}
  </span>
);

const CONTENT_TYPES = [
  { key: 'commercial', label: 'Commercial', hint: 'Lengte moet veelvoud van 5 s zijn' },
  { key: 'billboard', label: 'Billboard', hint: '5-seconden-regel niet van toepassing' },
  { key: 'promo', label: 'Promo', hint: '5-seconden-regel niet van toepassing' },
];

const STATUS = {
  pass: { icon: 'check_circle', color: T.pass, bg: T.passBg, label: 'OK' },
  fail: { icon: 'cancel', color: T.fail, bg: T.failBg, label: 'Fout' },
  warn: { icon: 'warning', color: T.warn, bg: T.warnBg, label: 'Let op' },
  undetermined: { icon: 'help', color: T.warn, bg: T.warnBg, label: 'Niet vastgesteld' },
  manual: { icon: 'visibility', color: T.neutral, bg: T.neutralBg, label: 'Handmatig' },
  skip: { icon: 'remove', color: T.neutral, bg: T.neutralBg, label: 'N.v.t.' },
};

const OVERALL = {
  pass: { icon: 'verified', color: T.pass, bg: T.passBg, title: 'Geschikt voor upload',
    text: 'Alle automatische controles zijn geslaagd. Loop de handmatige checklist nog even na.' },
  warn: { icon: 'error', color: T.warn, bg: T.warnBg, title: 'Aandachtspunten gevonden',
    text: 'Geen harde afkeuringen, maar er zijn punten die je beter controleert vóór aanlevering.' },
  incomplete: { icon: 'help', color: T.warn, bg: T.warnBg, title: 'Niet volledig vastgesteld',
    text: 'Een of meer metingen zijn mislukt. Zonder die metingen kunnen we niet zeggen dat het bestand geschikt is.' },
  fail: { icon: 'gpp_bad', color: T.fail, bg: T.failBg, title: 'Niet geschikt — wordt afgekeurd',
    text: 'Eén of meer verplichte eisen worden niet gehaald. Los de fouten hieronder op en check opnieuw.' },
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function leesFout(res) {
  try {
    const data = await res.json();
    return data.detail || data.error || `Fout ${res.status}`;
  } catch {
    return `Fout ${res.status}`;
  }
}

export default function MXFCheck() {
  const [file, setFile] = useState(null);
  const [contentType, setContentType] = useState('commercial');
  const [lowLoudness, setLowLoudness] = useState(false);
  const [multiChannel, setMultiChannel] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | uploading | analysing
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const runRef = useRef(0); // verhoogd bij elke run en bij unmount: oudere antwoorden negeren

  const busy = phase !== 'idle';

  useEffect(() => () => { runRef.current += 1; }, []);

  // Elke invoerwijziging maakt het vorige resultaat ongeldig.
  const wijzig = (setter) => (value) => {
    if (busy) return;
    setter(value);
    setResult(null);
  };

  const pickFile = (f) => {
    if (!f || busy) return;
    if (!f.name.toLowerCase().endsWith('.mxf')) {
      toast.error('Kies een .mxf-bestand');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Bestand is groter dan ${MAX_MB} MB`);
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const analyze = useCallback(async () => {
    if (!file || busy) return;
    const run = ++runRef.current;
    const actueel = () => runRef.current === run;
    setResult(null);
    setPhase('uploading');
    try {
      const params = new URLSearchParams({
        filename: file.name,
        content_type: contentType,
        low_loudness: String(lowLoudness),
        multi_channel_audio: String(multiChannel),
      });
      const res = await authFetch(`${BACKEND_URL}/Api/mxf-check/analyses?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) throw new Error(await leesFout(res));
      const { job_id: jobId } = await res.json();
      if (!actueel()) return;
      setPhase('analysing');

      const start = Date.now();
      while (actueel()) {
        if (Date.now() - start > MAX_POLL_MS) throw new Error('De analyse duurt ongewoon lang; probeer het opnieuw.');
        await sleep(POLL_MS);
        if (!actueel()) return;
        const st = await authFetch(`${BACKEND_URL}/Api/mxf-check/analyses/${jobId}`);
        if (!st.ok) throw new Error(await leesFout(st));
        const job = await st.json();
        if (!actueel()) return;
        if (job.status === 'error') throw new Error(job.error || 'Analyse mislukt');
        if (job.status === 'done') {
          setResult(job.result);
          const o = job.result?.overall;
          if (o === 'pass') toast.success('Geschikt voor upload');
          else if (o === 'fail') toast.error('Niet geschikt — zie details');
          else toast('Controleer de details', { icon: '⚠️' });
          return;
        }
      }
    } catch (err) {
      if (actueel()) toast.error(err.message || 'Analyse mislukt');
    } finally {
      if (actueel()) setPhase('idle');
    }
  }, [file, busy, contentType, lowLoudness, multiChannel]);

  return (
    <Box sx={{ width: '100%', bgcolor: T.bg, minHeight: 'calc(100vh - 44px)' }}>
      <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 8 }}>
        <PageHeader
          icon="live_tv"
          title="MXF Check"
          subtitle="Controleer of je MXF voldoet aan de Screenforce/SpotDeli aanleverspecificaties"
          accent={{ bg: T.primaryContainer, color: T.primary }}
        />

        {/* Contenttype */}
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.95rem', color: T.text, mb: 1.2 }}>
          Contenttype
        </Typography>
        <Box role="radiogroup" aria-label="Contenttype" sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mb: 2 }}>
          {CONTENT_TYPES.map((ct) => {
            const active = contentType === ct.key;
            return (
              <Box key={ct.key} role="radio" aria-checked={active} aria-disabled={busy}
                tabIndex={busy ? -1 : 0}
                onClick={() => wijzig(setContentType)(ct.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); wijzig(setContentType)(ct.key); } }}
                sx={{
                  cursor: busy ? 'default' : 'pointer', px: 2, py: 1.2, borderRadius: '0.75rem', flex: '1 1 200px',
                  border: `1.5px solid ${active ? T.primary : T.outlineVariant}`,
                  bgcolor: active ? T.primaryContainer : T.surface,
                  opacity: busy && !active ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}>
                <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.9rem', color: T.text }}>
                  {ct.label}
                </Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.secondary }}>
                  {ct.hint}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Metadata die je in SpotDeli opgeeft */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
          <FormControlLabel disabled={busy}
            control={<Checkbox size="small" checked={lowLoudness} onChange={(e) => wijzig(setLowLoudness)(e.target.checked)} />}
            label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text }}>
              Bewust zacht: LOW_LOUDNESS_LEVEL = TRUE in het metadataformulier (§2.3.5)</Typography>} />
          <FormControlLabel disabled={busy}
            control={<Checkbox size="small" checked={multiChannel} onChange={(e) => wijzig(setMultiChannel)(e.target.checked)} />}
            label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text }}>
              Surround op kanaal 3–8: MULTI_CHANNEL_AUDIO in het metadataformulier (§2.3.3)</Typography>} />
        </Box>

        {/* Dropzone */}
        <Box
          data-testid="mxf-dropzone"
          aria-disabled={busy}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => { if (!busy) inputRef.current?.click(); }}
          sx={{
            cursor: busy ? 'default' : 'pointer', borderRadius: '1rem', p: 4, textAlign: 'center',
            border: `2px dashed ${dragOver ? T.primary : T.outlineVariant}`,
            bgcolor: dragOver ? T.primaryContainer : T.surface, transition: 'all 0.15s', mb: 2,
            opacity: busy ? 0.6 : 1,
          }}>
          <input ref={inputRef} type="file" accept=".mxf" hidden disabled={busy}
            data-testid="mxf-input"
            onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }} />
          <Icon name={file ? 'movie' : 'upload_file'} size={40} sx={{ color: T.primary }} />
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, mt: 1 }}>
            {file ? file.name : 'Sleep een .mxf hierheen of klik om te kiezen'}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>
            {file ? `${(file.size / 1_048_576).toFixed(1)} MB` : `Max ${MAX_MB} MB`}
          </Typography>
        </Box>

        <Button
          onClick={analyze} disabled={!file || busy}
          variant="contained" fullWidth
          sx={{
            textTransform: 'none', fontFamily: T.font, fontWeight: 700, fontSize: '0.95rem',
            py: 1.4, borderRadius: '0.75rem', bgcolor: T.primary,
            '&:hover': { bgcolor: '#465469' },
          }}>
          {busy
            ? <><CircularProgress size={18} sx={{ color: '#fff', mr: 1.2 }} /> {phase === 'uploading' ? 'Uploaden…' : 'Analyseren…'}</>
            : 'Controleer MXF'}
        </Button>
        {busy && (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.secondary, textAlign: 'center', mt: 1 }}>
            {phase === 'uploading'
              ? 'Het bestand wordt geüpload; bij grote bestanden kan dat even duren.'
              : 'Beeld en geluid worden gemeten. Dit duurt meestal minder dan een minuut.'}
          </Typography>
        )}

        {result && <Results result={result} />}
      </Box>
    </Box>
  );
}

function Results({ result }) {
  const o = OVERALL[result.overall] || OVERALL.incomplete;
  const s = result.summary || {};

  return (
    <Box sx={{ mt: 4 }} data-testid="mxf-result">
      {/* Overall banner */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: 2.5, borderRadius: '1rem',
        bgcolor: o.bg, border: `1.5px solid ${o.color}33`, mb: 2 }}>
        <Icon name={o.icon} fill size={30} sx={{ color: o.color }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.15rem', color: o.color }}>
            {o.title}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>
            {o.text}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 1.2 }}>
            <Count n={s.pass} label="OK" color={T.pass} bg={T.passBg} />
            <Count n={s.fail} label="Fout" color={T.fail} bg={T.failBg} />
            {s.undetermined ? <Count n={s.undetermined} label="Niet vastgesteld" color={T.warn} bg={T.warnBg} /> : null}
            <Count n={s.warn} label="Let op" color={T.warn} bg={T.warnBg} />
            {s.skip ? <Count n={s.skip} label="N.v.t." color={T.neutral} bg={T.neutralBg} /> : null}
          </Box>
        </Box>
      </Box>

      {/* Media samenvatting */}
      {result.media && (
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 2 }}>
          {result.filename && <MetaChip label={result.filename} />}
          {result.media.duration_s != null && <MetaChip label={`${result.media.duration_s}s`} />}
          {result.media.resolution && <MetaChip label={result.media.resolution} />}
          {result.media.fps && <MetaChip label={`${result.media.fps} fps`} />}
          {result.media.field_order && <MetaChip label={result.media.field_order === 'tt' ? 'TFF' : result.media.field_order} />}
          {result.media.audio_layout && <MetaChip label={result.media.audio_layout} />}
        </Box>
      )}

      {/* Checks */}
      <Box sx={{ borderRadius: '1rem', bgcolor: T.surface, boxShadow: T.shadow, overflow: 'hidden' }}>
        {(result.checks || []).map((c, i) => <CheckRow key={c.id || i} c={c} last={i === result.checks.length - 1} />)}
      </Box>

      {/* Handmatige checklist */}
      {result.manual_checklist?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.95rem', color: T.text, mb: 1 }}>
            Nog handmatig te controleren
          </Typography>
          <Box sx={{ borderRadius: '1rem', bgcolor: T.surface, boxShadow: T.shadow, overflow: 'hidden' }}>
            {result.manual_checklist.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.8,
                borderTop: i ? `1px solid ${T.surfaceHigh}` : 'none' }}>
                <Icon name="visibility" size={20} sx={{ color: T.neutral, mt: '2px' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>{m.label}</Typography>
                  <Typography sx={{ fontFamily: T.fontMono, fontSize: '0.7rem', color: T.secondary }}>§{m.spec_ref}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function CheckRow({ c, last }) {
  const [open, setOpen] = useState(false);
  const st = STATUS[c.status] || STATUS.undetermined;
  const hasFix = c.fix && (c.status === 'fail' || c.status === 'warn' || c.status === 'undetermined');

  return (
    <Box sx={{ borderBottom: last ? 'none' : `1px solid ${T.surfaceHigh}` }}>
      <Box onClick={() => hasFix && setOpen((v) => !v)}
        sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.8, cursor: hasFix ? 'pointer' : 'default' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '0.6rem', bgcolor: st.bg, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={st.label}>
          <Icon name={st.icon} fill size={19} sx={{ color: st.color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 600, fontSize: '0.88rem', color: T.text }}>
            {c.label}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.76rem', color: T.secondary }}>
            <span style={{ fontFamily: T.fontMono }}>§{c.spec_ref}</span>
            {c.actual ? ` · ${c.actual}` : ''}{c.expected && c.status === 'fail' ? ` (verwacht: ${c.expected})` : ''}
          </Typography>
        </Box>
        {hasFix && <Icon name={open ? 'expand_less' : 'expand_more'} size={22} sx={{ color: T.secondary }} />}
      </Box>
      {hasFix && (
        <Collapse in={open}>
          <Box sx={{ px: 1.8, pb: 1.8, ml: '42px' }}>
            <Box sx={{ p: 1.5, borderRadius: '0.6rem', bgcolor: T.surfaceLow, borderLeft: `3px solid ${st.color}` }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, whiteSpace: 'pre-wrap' }}>
                <strong>{c.status === 'undetermined' ? 'Waarom niet vastgesteld:' : 'Oplossen:'}</strong> {c.fix}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

const Count = ({ n, label, color, bg }) => (
  <Box sx={{ px: 1.2, py: 0.4, borderRadius: '0.5rem', bgcolor: bg }}>
    <Typography component="span" sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '0.82rem', color }}>
      {n || 0}
    </Typography>
    <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color, ml: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

const MetaChip = ({ label }) => (
  <Chip label={label} size="small"
    sx={{ fontFamily: T.fontMono, fontSize: '0.72rem', bgcolor: T.surfaceHigh, color: T.text, borderRadius: '0.5rem' }} />
);
